'use client';

import { useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { Expand } from 'lucide-react';
import { Item } from '@/lib/api';
import { useModalHelpers, useSignInModal } from '@/lib/ui';
import { useSession } from '@/lib/auth-client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useItemDetailSheet } from '@/lib/hooks/useItemDetailSheet';
import { LazyImage } from './LazyImage';
import { CompactReactionButton } from './activities/CompactReactionButton';
import { ImageLoader } from './ImageLoader';

interface ItemCardProps {
  item: Item;
  onAddToCollection?: (itemId: string) => void;
  showTags?: boolean;
  showPrice?: boolean;
  variant?: 'default' | 'compact';
}

export function ItemCard({
  showTags,
  item,
  onAddToCollection,
  variant = 'default',
  showPrice = false,
}: ItemCardProps) {
  const modalHelpers = useModalHelpers();
  const [isHovered, setIsHovered] = useState(false);
  const { showSignInModal } = useSignInModal();
  const { showItemDetail } = useItemDetailSheet();
  const { data: session } = useSession();
  const isMobile = useIsMobile();
  const isApproved = !!session?.user?.approved;
  const isPending = !!session?.user?.isPending;
  const isCompact = variant === 'compact';

  // Check if item is in any collection from API data
  const isInCollection = item.userCollections && item.userCollections.length > 0;
  const collectionCount = item.userCollections?.length || 0;

  // Check if user has edit permissions for this item's brand
  const canEditProduct = item.brandPermissions?.permissions.includes('edit_products') || false;

  const formatPrice = (price: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
  };

  const handleShowDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showItemDetail(item.id);
  };
  return (
    <Link href={`/items/${item.id}`}>
      <div
        className={clsx(
          'group relative bg-white overflow-hidden hover:shadow-xl cursor-pointer',
          isCompact ? 'rounded-xl' : 'rounded-2xl'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative overflow-hidden">
          <ImageLoader
            width={200}
            height={200}
            src={item.image || ''}
            alt={item.title}
            aspectRatio={isCompact ? '4/3' : '3/4'}
            className={clsx(
              'bg-white w-full',
              isCompact ? 'h-[140px]' : 'h-[300px]',
              isHovered && 'scale-105'
            )}
            style={{ objectFit: 'contain' }}
            fallback={
              <div
                className={clsx(
                  'flex items-center justify-center bg-white',
                  isCompact ? 'h-[140px]' : 'h-[300px]'
                )}
              >
                <div className="text-center p-2">
                  <svg
                    className={clsx(
                      'mx-auto text-gray-400 mb-1',
                      isCompact ? 'w-6 h-6' : 'w-12 h-12'
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className={clsx('text-gray-500', isCompact ? 'text-[10px]' : 'text-xs')}>
                    Image unavailable
                  </p>
                </div>
              </div>
            }
          />
          <div
            className={clsx(
              'absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300'
            )}
          />
          {item.isNew && (
            <span
              className={clsx(
                'absolute bg-emerald-500 text-white font-semibold rounded-full shadow-lg z-10',
                isCompact
                  ? 'top-2 left-2 text-[10px] px-1.5 py-0.5'
                  : 'top-3 left-3 text-xs px-2.5 py-1'
              )}
            >
              NEW
            </span>
          )}
          <button
            onClick={handleShowDetails}
            className={clsx(
              'absolute rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200',
              isCompact ? 'top-2 left-2 w-7 h-7' : 'top-3 left-3 w-10 h-10',
              'opacity-100 translate-y-0',
              'bg-white/95 backdrop-blur-sm hover:bg-primary-500 hover:text-white'
            )}
            aria-label="Show details"
            title="Show details"
          >
            <Expand className={clsx(isCompact ? 'w-3 h-3' : 'w-5 h-5')} />
          </button>
          {canEditProduct && !isCompact && (
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // TODO: Implement edit functionality
                  modalHelpers.showModal({
                    title: 'Edit Product',
                    component: 'Edit functionality coming soon',
                  });
                }}
                className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200 bg-white/95 backdrop-blur-sm hover:bg-blue-500 hover:text-white"
                aria-label="Edit product"
                title="Edit product"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // TODO: Implement delete functionality
                  modalHelpers.showModal({
                    title: 'Delete Product',
                    component: 'Delete functionality coming soon',
                  });
                }}
                className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200 bg-white/95 backdrop-blur-sm hover:bg-red-500 hover:text-white"
                aria-label="Delete product"
                title="Delete product"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          )}
          {showTags && item.tags && item.tags.length > 0 && !isCompact && (
            <div
              className={clsx(
                'absolute top-3 flex flex-wrap gap-1 justify-end max-w-[60%]',
                canEditProduct ? 'right-[120px]' : 'right-14'
              )}
            >
              {item.tags.slice(0, 2).map((tag) => (
                <Link
                  key={tag}
                  href={`/items?tags=${encodeURIComponent(tag)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs px-2 py-0.5 rounded-full hover:bg-white transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isApproved) onAddToCollection?.(item.id);
              else if (isPending)
                modalHelpers.showModal({
                  title: 'Account Approval Pending',
                  component: 'You will be able to perform collection operations once approved.',
                });
              else
                showSignInModal({
                  onSuccess() {
                    modalHelpers.showModal({
                      title: 'Account Approval Pending',
                      component: 'You will be able to perform collection operations once approved.',
                    });
                  },
                });
            }}
            className={clsx(
              'absolute rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all duration-200',
              isCompact ? 'bottom-2 right-2 w-7 h-7' : 'bottom-3 right-3 w-10 h-10',
              'opacity-100 translate-y-0',
              isInCollection
                ? 'bg-green-100 text-black hover:bg-red-600'
                : 'bg-white/95 backdrop-blur-sm hover:bg-primary-500 hover:text-white'
            )}
            aria-label={
              isInCollection
                ? `In ${collectionCount} collection${collectionCount !== 1 ? 's' : ''}`
                : 'Add to collection'
            }
            title={
              isInCollection
                ? `In ${collectionCount} collection${collectionCount !== 1 ? 's' : ''}`
                : 'Add to collection'
            }
          >
            <svg
              className={clsx(isCompact ? 'w-3 h-3' : 'w-5 h-5')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
          <CompactReactionButton
            objectId={item.id}
            objectType="item"
            variant={variant}
            title={item.title}
            userReactions={item.userReactions || []}
            engagementSummary={
              item.engagement || {
                reactions: { total: 0, by_type: {} },
                votes: { up: { count: 0, last_at: '' }, down: { count: 0, last_at: '' } },
              }
            }
            className={clsx(
              'absolute',
              isCompact ? 'bottom-2 left-2' : 'bottom-3 left-3',
              'opacity-100 translate-y-0'
            )}
          />
        </div>
        <div className={clsx(isCompact ? 'p-2' : 'p-4')}>
          {item.brand?.name_1 && (
            <p
              className={clsx(
                'text-gray-500 font-medium uppercase tracking-wide mb-1',
                isCompact ? 'text-[10px]' : 'text-xs'
              )}
            >
              {item.brand?.name_1}
            </p>
          )}
          <h3
            className={clsx(
              'font-semibold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors',
              'text-base'
            )}
          >
            {item.title}
          </h3>
          {item.description && !isCompact && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{item.description}</p>
          )}
          {showPrice && (
            <p className={clsx('font-bold text-primary-600', isCompact ? 'text-sm' : 'text-lg')}>
              {item.price_2 && item.price_2 !== item.price_1
                ? `${formatPrice(item.price_1, item.currency)} - ${formatPrice(item.price_2, item.currency)}`
                : formatPrice(item.price_1, item.currency)}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
